import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { trainingReportApi } from '../../services/trainingReportApi';
import { useData } from './DataContext';
import { getClases } from './constants';
import './AdminOperations.css';
import './TrainingReports.css';

const REPORTS_PER_PAGE = 10;

const formatDate = (value) => {
  if (!value) return 'Pendiente';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const TrainingReportsPage = () => {
  const navigate = useNavigate();
  const { data } = useData();
  const accessToken = data?.access_token;
  const [reports, setReports] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState(null);

  const loadData = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      setStatus({
        type: 'error',
        message:
          'La sesión administrativa no está disponible. Inicia sesión nuevamente para administrar reportes.',
      });
      return;
    }

    setLoading(true);
    setStatus(null);

    const [reportResult, classResult] = await Promise.allSettled([
      trainingReportApi.list(accessToken),
      axios.get(getClases),
    ]);

    const messages = [];

    if (reportResult.status === 'fulfilled') {
      setReports(Array.isArray(reportResult.value) ? reportResult.value : []);
    } else {
      console.error(
        'No se pudieron cargar los reportes de capacitación:',
        reportResult.reason,
      );
      setReports([]);
      messages.push(
        reportResult.reason?.message ||
          'No fue posible consultar los reportes de capacitación.',
      );
    }

    if (classResult.status === 'fulfilled') {
      setClasses(
        Array.isArray(classResult.value?.data) ? classResult.value.data : [],
      );
    } else {
      console.error('No se pudieron cargar las clases:', classResult.reason);
      setClasses([]);
      messages.push('No fue posible consultar las clases disponibles.');
    }

    if (messages.length > 0) {
      setStatus({
        type: 'error',
        message: messages.join(' '),
      });
    }

    setLoading(false);
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredReports = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reports;

    return reports.filter((report) =>
      [
        report.nombre_accion,
        report.matricula_clase,
        report.nombre_instructor,
        report.estado,
      ].some((value) => String(value || '').toLowerCase().includes(term)),
    );
  }, [reports, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReports.length / REPORTS_PER_PAGE),
  );
  const pageReports = filteredReports.slice(
    (page - 1) * REPORTS_PER_PAGE,
    page * REPORTS_PER_PAGE,
  );

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const createReport = async () => {
    if (!selectedClassId || !accessToken) return;

    setCreating(true);
    setStatus(null);

    try {
      const report = await trainingReportApi.create(
        Number(selectedClassId),
        accessToken,
      );
      navigate(`/a-reportes-capacitacion/${report.id_reporte}`);
    } catch (error) {
      console.error('No se pudo crear el reporte:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible crear el reporte.',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="admin-operation-page training-reports-page">
      <main className="admin-operation-container">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Documentación de capacitación</p>
          <h1>Reportes de capacitación</h1>
          <p>
            Crea, retoma y consulta el reporte formal de cada acción de
            capacitación antes de generar su PDF definitivo.
          </p>
        </header>

        <section className="admin-operation-panel">
          <div className="admin-operation-panel-heading">
            <div>
              <h2>Nuevo reporte</h2>
              <p>
                Selecciona una clase. El borrador tomará una fotografía de la
                clase, instructor y alumnos actuales.
              </p>
            </div>
          </div>

          <div className="training-report-create-row">
            <div className="admin-operation-field">
              <label htmlFor="training-report-class">Clase</label>
              <select
                id="training-report-class"
                value={selectedClassId}
                onChange={(event) => setSelectedClassId(event.target.value)}
                disabled={loading || creating || !accessToken}
              >
                <option value="">Selecciona una clase</option>
                {classes.map((item) => (
                  <option key={item.id_clase} value={item.id_clase}>
                    {item.matricula_clase} - {item.nombre_clase}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="admin-operation-button"
              type="button"
              onClick={createReport}
              disabled={!selectedClassId || creating || !accessToken}
            >
              {creating ? 'Creando borrador...' : 'Crear reporte'}
            </button>
          </div>
        </section>

        <section className="admin-operation-panel">
          <div className="admin-operation-toolbar">
            <input
              className="admin-operation-search"
              type="search"
              placeholder="Buscar por acción, clase, instructor o estado"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              aria-label="Buscar reporte de capacitación"
            />
            <div className="admin-operation-toolbar-actions">
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={loadData}
                disabled={loading || creating || !accessToken}
              >
                Actualizar listado
              </button>
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={() => navigate('/admin-home')}
                disabled={creating}
              >
                Regresar al inicio
              </button>
            </div>
          </div>

          {status && (
            <div
              className={`admin-operation-status admin-operation-status-${status.type}`}
              role="status"
            >
              {status.message}
            </div>
          )}

          <p className="admin-operation-count">
            {loading
              ? 'Consultando reportes...'
              : `${filteredReports.length} reporte${filteredReports.length === 1 ? '' : 's'}`}
          </p>

          {!loading && pageReports.length === 0 ? (
            <p className="admin-operation-empty">
              Todavía no hay reportes que coincidan con la búsqueda.
            </p>
          ) : (
            <div className="admin-operation-table-wrapper">
              <table className="admin-operation-table training-reports-table">
                <thead>
                  <tr>
                    <th>Acción</th>
                    <th>Clase</th>
                    <th>Instructor</th>
                    <th>Periodo</th>
                    <th>Participantes</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {pageReports.map((report) => (
                    <tr key={report.id_reporte}>
                      <td>{report.nombre_accion || 'Sin nombre'}</td>
                      <td>{report.matricula_clase || `#${report.id_clase}`}</td>
                      <td>{report.nombre_instructor || 'Pendiente'}</td>
                      <td>
                        {formatDate(report.fecha_inicio)} -{' '}
                        {formatDate(report.fecha_fin)}
                      </td>
                      <td>{report.total_participantes ?? 0}</td>
                      <td>
                        <span
                          className={`training-report-status training-report-status-${report.estado || 'borrador'}`}
                        >
                          {report.estado === 'generado' ? 'Generado' : 'Borrador'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="admin-operation-button admin-operation-button-compact"
                          type="button"
                          onClick={() =>
                            navigate(`/a-reportes-capacitacion/${report.id_reporte}`)
                          }
                        >
                          {report.estado === 'generado' ? 'Ver reporte' : 'Continuar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredReports.length > 0 && (
            <div className="admin-operation-pagination">
              <button
                className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                type="button"
                disabled={page === 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Anterior
              </button>
              <span>
                Página {page} de {totalPages}
              </span>
              <button
                className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
